import React, { startTransition, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { fetchReviewCases } from '../utils/agentUiApi';
import {
  DEFAULT_LIMIT,
  DEFAULT_STATUS,
  LIMIT_OPTIONS,
  STATUS_OPTIONS,
  ReviewChip,
  buildFilterState,
  buildSearchParams,
  filterReviewCases,
  formatDateTime,
  formatDomainValue,
  formatRelativeSync,
  formatRisk,
  formatStatus,
  formatStoplineSummary,
  normalizeIntegerFilter,
  normalizeMetadata,
  resolveMetadataValue,
  riskTone,
  statusTone,
  trimText,
} from './ReviewQueueCommon';
import './ReviewQueuePage.css';

function SummaryCard({ label, value, hint }) {
  return (
    <article className="review-queue-summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{hint}</small>
    </article>
  );
}

function ReviewCaseRow({ item, onOpen }) {
  const metadata = normalizeMetadata(item.metadata_json);

  return (
    <button
      type="button"
      className="review-queue-list-item"
      onClick={() => onOpen(item.case_id)}
    >
      <div className="review-queue-list-head">
        <div>
          <strong>{item.case_no || item.case_id}</strong>
          <span>{formatDateTime(item.created_at)}</span>
        </div>
        <div className="review-queue-chip-row">
          <ReviewChip tone={statusTone(item.status)}>{formatStatus(item.status)}</ReviewChip>
          <ReviewChip type="risk" tone={riskTone(item.risk_level)}>{formatRisk(item.risk_level)}</ReviewChip>
        </div>
      </div>

      <p>{trimText(formatStoplineSummary(item.summary_text), 220)}</p>

      <div className="review-queue-list-meta">
        <span>사용자 {item.user_no ?? '-'}</span>
        <span>환자 {item.patient_id ?? '-'}</span>
        <span>담당 {item.assigned_to || '-'}</span>
        <span>우선순위 {formatDomainValue(item.priority)}</span>
        <span>사유 {resolveMetadataValue(metadata, 'reason')}</span>
        <span>RAG {resolveMetadataValue(metadata, 'rag_status')}</span>
      </div>
    </button>
  );
}

function ReviewQueuePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilters = useMemo(() => buildFilterState(searchParams), [searchParams]);

  const [filters, setFilters] = useState(() => buildFilterState(searchParams));
  const [quickFilter, setQuickFilter] = useState('');
  const deferredQuickFilter = useDeferredValue(quickFilter);
  const [reviewCases, setReviewCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [refreshSeed, setRefreshSeed] = useState(0);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  useEffect(() => {
    setFilters(activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    let cancelled = false;

    async function loadQueue() {
      setLoading(true);
      setRefreshing(true);
      setError('');

      try {
        const response = await fetchReviewCases({
          status: activeFilters.status || undefined,
          userNo: activeFilters.userNo || undefined,
          patientId: activeFilters.patientId || undefined,
          limit: Number(activeFilters.limit || DEFAULT_LIMIT),
          offset: 0,
        });
        if (cancelled) {
          return;
        }
        setReviewCases(Array.isArray(response?.items) ? response.items : []);
        setLastLoadedAt(Date.now());
      } catch (loadError) {
        if (!cancelled) {
          setReviewCases([]);
          setError(loadError.message || '검토 대기열을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }

    loadQueue();
    return () => {
      cancelled = true;
    };
  }, [
    activeFilters.status,
    activeFilters.userNo,
    activeFilters.patientId,
    activeFilters.limit,
    refreshSeed,
  ]);

  const visibleCases = useMemo(
    () => filterReviewCases(reviewCases, deferredQuickFilter),
    [reviewCases, deferredQuickFilter]
  );
  const pendingCount = reviewCases.filter((item) => item.status === 'pending_triage').length;
  const elevatedRiskCount = reviewCases.filter((item) => ['medium', 'high'].includes(item.risk_level)).length;
  const unresolvedCount = reviewCases.filter((item) => !['resolved', 'dismissed'].includes(item.status)).length;

  const handleApplyFilters = (event) => {
    event.preventDefault();
    startTransition(() => {
      setSearchParams(buildSearchParams(filters));
    });
  };

  const handleResetFilters = () => {
    const nextFilters = {
      status: DEFAULT_STATUS,
      userNo: '',
      patientId: '',
      limit: DEFAULT_LIMIT,
    };
    setFilters(nextFilters);
    setQuickFilter('');
    startTransition(() => {
      setSearchParams(buildSearchParams(nextFilters));
    });
  };

  const handleOpenCase = (caseId) => {
    if (!caseId) {
      return;
    }
    const query = searchParams.toString();
    navigate({
      pathname: `/review/${encodeURIComponent(caseId)}`,
      search: query ? `?${query}` : '',
    });
  };

  return (
    <div className="review-queue-page">
      <section className="review-queue-hero review-queue-hero-compact">
        <div>
          <p className="review-queue-eyebrow">CRV-UX01</p>
          <h1>임상 검토 대기열</h1>
          <p className="review-queue-hero-copy">
            Stop-the-line에 걸린 케이스를 빠르게 선별합니다. 상세 근거 확인과 후속 조치는 케이스 상세 화면에서 수행합니다.
          </p>
        </div>
        <div className="review-queue-hero-actions">
          <button
            type="button"
            className="review-queue-refresh-button"
            onClick={() => setRefreshSeed((current) => current + 1)}
            disabled={refreshing}
          >
            {refreshing ? '새로고침 중...' : '대기열 새로고침'}
          </button>
          <div className="review-queue-sync-block">
            <strong>{formatRelativeSync(lastLoadedAt)}</strong>
            <span>{lastLoadedAt ? formatDateTime(lastLoadedAt) : '아직 동기화되지 않음'}</span>
          </div>
        </div>
      </section>

      <section className="review-queue-summary-grid">
        <SummaryCard label="대기열 건수" value={reviewCases.length} hint="현재 필터 결과" />
        <SummaryCard label="분류 대기" value={pendingCount} hint="초기 확인 필요" />
        <SummaryCard label="중간 이상 위험" value={elevatedRiskCount} hint="중간 + 높음" />
        <SummaryCard
          label="미해결"
          value={unresolvedCount}
          hint={activeFilters.status ? `${formatStatus(activeFilters.status)} 기준` : '전체 상태 기준'}
        />
      </section>

      {error ? <div className="review-queue-error">{error}</div> : null}

      <section className="review-queue-layout review-queue-master-layout">
        <aside className="review-queue-sidebar">
          <article className="review-queue-card">
            <div className="review-queue-card-head">
              <h2>대기열 필터</h2>
              <p>상태, 사용자 번호, 환자 ID로 실시간 대기열 범위를 좁힙니다.</p>
            </div>
            <form className="review-queue-filter-form" onSubmit={handleApplyFilters}>
              <label>
                <span>상태</span>
                <select
                  value={filters.status}
                  onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.label} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>사용자 번호</span>
                <input
                  type="number"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="비워두면 전체"
                  value={filters.userNo}
                  onChange={(event) => setFilters((current) => ({
                    ...current,
                    userNo: normalizeIntegerFilter(event.target.value),
                  }))}
                />
              </label>

              <label>
                <span>환자 ID</span>
                <input
                  type="number"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="비워두면 전체"
                  value={filters.patientId}
                  onChange={(event) => setFilters((current) => ({
                    ...current,
                    patientId: normalizeIntegerFilter(event.target.value),
                  }))}
                />
              </label>

              <label>
                <span>페이지 크기</span>
                <select
                  value={filters.limit}
                  onChange={(event) => setFilters((current) => ({ ...current, limit: event.target.value }))}
                >
                  {LIMIT_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>

              <div className="review-queue-button-row">
                <button type="submit">필터 적용</button>
                <button type="button" className="secondary" onClick={handleResetFilters}>초기화</button>
              </div>
            </form>
          </article>
        </aside>

        <main className="review-queue-main">
          <article className="review-queue-card">
            <div className="review-queue-card-head review-queue-toolbar">
              <div>
                <h2>검토 케이스 목록</h2>
                <p>먼저 확인할 stop-the-line 케이스를 선택합니다.</p>
              </div>
              <label className="review-queue-search">
                <span>빠른 검색</span>
                <input
                  type="text"
                  value={quickFilter}
                  onChange={(event) => setQuickFilter(event.target.value)}
                  placeholder="케이스 번호, DEC ID, 사유"
                />
              </label>
            </div>

            {loading ? (
              <div className="review-queue-empty">검토 대기열을 불러오는 중입니다...</div>
            ) : visibleCases.length ? (
              <div className="review-queue-list">
                {visibleCases.map((item) => (
                  <ReviewCaseRow key={item.case_id} item={item} onOpen={handleOpenCase} />
                ))}
              </div>
            ) : (
              <div className="review-queue-empty">
                현재 필터와 빠른 검색 조건에 맞는 검토 케이스가 없습니다.
              </div>
            )}
          </article>
        </main>
      </section>
    </div>
  );
}

export default ReviewQueuePage;
